import { Routes } from '@angular/router';
import {
  AuthenticationValidationGuard,
  CancelNavigationGuard,
  DesktopOnlyGuard,
  ProPlanValidationGuard,
  ProPlusValidationGuard,
  SubscriptionValidationGuard
} from '@App/common/guards';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { PitchCardComponent } from './pages/pitch-card-display/pitch-card.component';
import { pitchCardResolver } from './pages/pitch-card-display/pitch-card.resolver';

export const routes: Routes = [
  {
    path: 'welcome',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: 'tenant-invite',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      title: 'Pitch59 | Stand Out. Be Remembered. Get More Referrals.',
      description:
        "PitchCards are the world's most powerful business card for networking, introductions, and referrals. Create your 59-second elevator pitch and grow your business."
    }
  },
  {
    path: '',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      title: 'Pitch59 | Stand Out. Be Remembered. Get More Referrals.',
      description:
        "PitchCards are the world's most powerful business card for networking, introductions, and referrals. Create your 59-second elevator pitch and grow your business."
    }
  },
  {
    path: 'login',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      authAction: 'SIGN_IN',
      title: 'Pitch59 | Log In'
    }
  },
  {
    path: 'signup',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      authAction: 'SIGN_UP',
      title: 'Pitch59 | Sign Up'
    }
  },
  {
    path: 'card/:alias/embeded',
    redirectTo: ({ params }) => `/card/${encodeURIComponent(params['alias'])}`,
    pathMatch: 'full'
  },
  {
    path: 'card/:alias',
    component: PitchCardComponent,
    data: { hideAppShell: true, managePageTitle: false },
    resolve: { card: pitchCardResolver }
  },
  {
    path: 'embedded-card/:alias',
    loadComponent: () =>
      import('./pages/embedded-pitchcard/embedded-pitchcard.component').then(
        (c) => c.EmbeddedPitchcardComponent
      ),
    data: { hideAppShell: true }
  },
  {
    path: 'reset-password',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      title: 'Pitch59 | Stand Out. Be Remembered. Get More Referrals.',
      description:
        "PitchCards are the world's most powerful business card for networking, introductions, and referrals. Create your 59-second elevator pitch and grow your business."
    }
  },
  {
    path: 'two-step-verification',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/two-step-verification/two-step-verification.component').then(
        (c) => c.TwoStepVerificationComponent
      )
  },
  {
    path: 'one-time-password',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/one-time-password/one-time-password.component').then(
        (c) => c.OneTimePasswordComponent
      )
  },
  {
    path: 'forgot-password',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(
        (c) => c.ForgotPasswordComponent
      )
  },
  {
    path: 'change-password',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/change-password/change-password.component').then(
        (c) => c.ChangePasswordComponent
      )
  },
  {
    path: 'jobs',
    pathMatch: 'full',
    component: LandingPageComponent,
    data: {
      title: 'Pitch59 | Get Noticed. Get Hired.',
      description:
        'PitchCards are the most powerful networking tool for job seekers. Share your personalized resume card and get referrals in just 5 seconds.'
    }
  },
  {
    path: 'affiliate',
    loadComponent: () =>
      import('./modules/affiliate-flow/pages/affiliate-create/affiliate-create.component').then(
        (c) => c.AffiliateCreateComponent
      )
  },
  {
    path: 'search',
    pathMatch: 'full',
    loadComponent: () =>
      import('@App/modules/search/components/search-result/search-result.component').then(
        (c) => c.SearchResultComponent
      ),
    canActivate: [AuthenticationValidationGuard, ProPlanValidationGuard]
  },
  {
    path: '404',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/404/404.component').then((c) => c.NotFoundPageComponent)
  },
  {
    path: 'terms-and-conditions',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/terms-and-conditions/terms-and-conditions.component').then(
        (c) => c.TermsAndConditionsComponent
      )
  },
  {
    path: 'affiliate-terms-and-conditions',
    loadComponent: () =>
      import('./pages/affiliate-terms/affiliate-terms.component').then(
        (c) => c.AffiliateTermsAndConditionsComponent
      )
  },
  {
    path: 'privacy-policy',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/privacy-policy/privacy-policy.component').then(
        (c) => c.PrivacyPolicyComponent
      )
  },
  {
    path: 'help-center',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/help-center/help-center.component').then(
        (c) => c.HelpCenterComponent
      ),
    data: {
      title: 'Help Center | Pitch59'
    }
  },
  {
    path: 'pricing',
    canActivate: [],
    loadComponent: () =>
      import('./pages/pricing/pricing-layout/pricing-layout.component').then(
        (c) => c.PricingLayoutComponent
      ),
    data: {
      title: 'PitchCards Pricing - More Than a Digital Business Card'
    }
  },
  {
    path: 'additional-cards',
    canActivate: [ProPlusValidationGuard],
    loadComponent: () =>
      import('./pages/pricing/pricing-layout/pricing-layout.component').then(
        (c) => c.PricingLayoutComponent
      ),
    data: {
      title: 'Additional PitchCards'
    }
  },
  {
    path: 'create',
    canActivate: [AuthenticationValidationGuard],
    loadComponent: () =>
      import('./modules/create-pitch-card/layout/layout.component').then(
        (c) => c.LayoutComponent
      )
  },
  {
    path: 'create/:step',
    canActivate: [AuthenticationValidationGuard],
    loadComponent: () =>
      import('./modules/create-pitch-card/layout/layout.component').then(
        (c) => c.LayoutComponent
      )
  },
  {
    path: 'account',
    children: [
      {
        path: 'pockets',
        loadComponent: () =>
          import('./modules/choosen-history/modules/my-pockets/my-pockets.component').then(
            (c) => c.MyPocketsComponent
          )
      },
      {
        path: 'pockets/:id',
        loadComponent: () =>
          import('./modules/choosen-history/modules/my-pockets/my-pockets.component').then(
            (c) => c.MyPocketsComponent
          )
      },
      {
        path: 'my-pitchcards',
        canActivate: [AuthenticationValidationGuard],
        loadComponent: () =>
          import('./modules/choosen-history/modules/pitch-cards/pitch-cards.component').then(
            (c) => c.PitchCardsComponent
          )
      },
      {
        path: 'my-pitchcards/:id',
        canActivate: [AuthenticationValidationGuard],
        loadComponent: () =>
          import('./modules/choosen-history/modules/pitch-cards/pitch-cards.component').then(
            (c) => c.PitchCardsComponent
          )
      },
      {
        path: 'my-subscriptions',
        canActivate: [
          AuthenticationValidationGuard,
          SubscriptionValidationGuard
        ],
        loadComponent: () =>
          import('./pages/pricing/manage-subscriptions/manage-subscriptions.component').then(
            (c) => c.ManageSubscriptionsComponent
          )
      },
      {
        path: 'employer-portal',
        canActivate: [AuthenticationValidationGuard, DesktopOnlyGuard],
        loadComponent: () =>
          import('./modules/choosen-history/modules/employer-portal/employer-portal.component').then(
            (c) => c.EmployerPortalComponent
          )
      },
      {
        path: 'affiliate-hub',
        canActivate: [AuthenticationValidationGuard],
        loadComponent: () =>
          import('./modules/affiliate/components/affiliate-hub/affiliate-hub.component').then(
            (c) => c.AffiliateHubComponent
          )
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./modules/choosen-history/modules/settings/settings.component').then(
            (c) => c.SettingsComponent
          )
      }
    ]
  },
  {
    path: 'billing-page',
    loadComponent: () =>
      import('./modules/new-billing/billing-page.component').then(
        (c) => c.BillingPageComponent
      ),
    children: [
      {
        path: 'billing-cycle',
        loadComponent: () =>
          import('./modules/new-billing/pages/billing-cycle/billing-cycle.component').then(
            (c) => c.BillingCycleComponent
          )
      },
      {
        path: 'virtual-video',
        loadComponent: () =>
          import('./modules/new-billing/pages/virtual-video/virtual-video.component').then(
            (c) => c.VirtualVideoComponent
          )
      },
      {
        path: 'billing-summary',
        loadComponent: () =>
          import('./modules/new-billing/pages/billing-summary/billing-summary.component').then(
            (c) => c.BillingSummaryComponent
          )
      }
    ]
  },
  {
    path: 'cancel-navigation',
    canActivate: [CancelNavigationGuard],
    loadComponent: () =>
      import('./pages/404/404.component').then((c) => c.NotFoundPageComponent),
    pathMatch: 'full'
  },
  {
    path: 'pocket/:token',
    loadComponent: () =>
      import('./pages/shared-pocket/shared-pocket/shared-pocket.component').then(
        (c) => c.SharedPocketComponent
      )
  },
  {
    path: '**',
    loadComponent: () =>
      import('./pages/404/404.component').then((c) => c.NotFoundPageComponent)
  }
];
